import { isNil } from "@etudescarre/utils";

type Role = string | null | undefined;

const YARD_SELECTION_PATH = "/organizations/select";

export function isStaffRole(role: Role): boolean {
  return role === "root" || role === "admin";
}

/**
 * Where an account lands after login or on the root URL. While staff
 * impersonates, the impersonated user's yard selection, whatever their role.
 */
export function homePathFor(role: Role, impersonated = false): string {
  return isStaffRole(role) && !impersonated ? "/staff" : YARD_SELECTION_PATH;
}

// A link back to any home page sends each account to its own home.
const HOME_PATHS = new Set([
  "/",
  "/home",
  "/staff",
  "/yards/select",
  YARD_SELECTION_PATH,
]);

/**
 * Where to go after login: the `redirect` link, unless it points to a home page
 * or to a staff or back-office page this account cannot open; then its home.
 * The link may have been left by another account signed in earlier in the same
 * tab. A yard link is followed: the yard sends an account that cannot open it
 * back to its home with the reason.
 */
export function resolveLoginDestination(
  redirect: string | null,
  role: Role,
): string {
  const home = homePathFor(role);
  if (isNil(redirect)) return home;

  const { pathname } = new URL(redirect, window.location.origin);
  if (HOME_PATHS.has(pathname)) return home;
  if (pathname.startsWith("/staff/") && !isStaffRole(role)) return home;
  if (/^\/admin(\/|$)/.test(pathname) && role !== "root") return home;

  return redirect;
}
